/**
 * Client-side mapper for the PQR preview's Notes overrides (slice 06).
 *
 * Strategy: this slice keeps the editor simple by reading only what is
 * persisted at `sections.pqr.overrides.notes`. The aggregated lists (built
 * server-side in `mapWelders` / `mapEquipment`) are intentionally NOT
 * mirrored here — replicating them would duplicate a lot of derivation code
 * and the PDF preview already shows the user the live aggregation in the
 * read-only section above. When no override is present the editor renders
 * an empty editable list with an "Add row" button; saving rows installs an
 * override; the "↺ Reset to aggregated" button sends a `null` override on
 * the next save which makes the renderer fall back to the aggregation.
 */

export interface PqrEditableNotesWelderRow {
  role: string
  name: string
}

export interface PqrEditableNotesEquipmentRow {
  label: string
  value: string
}

export interface PqrEditableNotesOverrideList<T> {
  /**
   * `true` when `sections.pqr.overrides.notes.<key>` exists (incl. empty
   * array). Drives the "Overridden — differs from aggregated …" badge
   * and the visibility of the "↺ Reset to aggregated" affordance.
   */
  isOverride: boolean
  rows: T[]
}

export interface PqrEditableNotes {
  welders: PqrEditableNotesOverrideList<PqrEditableNotesWelderRow>
  equipment: PqrEditableNotesOverrideList<PqrEditableNotesEquipmentRow>
}

function asObject(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : null
}

function readWelderRows(raw: unknown): PqrEditableNotesOverrideList<PqrEditableNotesWelderRow> {
  if (!Array.isArray(raw)) return { isOverride: false, rows: [] }
  const rows = raw
    .map((row) => {
      const obj = asObject(row)
      if (!obj) return null
      const role = typeof obj.role === 'string' ? obj.role : ''
      const name = typeof obj.name === 'string' ? obj.name : ''
      return { role, name }
    })
    .filter((r): r is PqrEditableNotesWelderRow => r !== null)
  return { isOverride: true, rows }
}

function readEquipmentRows(
  raw: unknown
): PqrEditableNotesOverrideList<PqrEditableNotesEquipmentRow> {
  if (!Array.isArray(raw)) return { isOverride: false, rows: [] }
  const rows = raw
    .map((row) => {
      const obj = asObject(row)
      if (!obj) return null
      const label = typeof obj.label === 'string' ? obj.label : ''
      const value = typeof obj.value === 'string' ? obj.value : ''
      return { label, value }
    })
    .filter((r): r is PqrEditableNotesEquipmentRow => r !== null)
  return { isOverride: true, rows }
}

export function readNotesFromSections(
  sections: Record<string, unknown> | null | undefined
): PqrEditableNotes {
  const safe = asObject(sections) ?? {}
  const pqr = asObject(safe.pqr)
  const overrides = asObject(pqr?.overrides)
  const notes = asObject(overrides?.notes)
  return {
    welders: readWelderRows(notes?.welders),
    equipment: readEquipmentRows(notes?.equipment)
  }
}
